import { Box, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import FormInput from "../Common/FormInputs/MuiTextField";
import Button from "../Common/Button/Button";
import IconButton from "../Common/Button/IconButton";
import {
  compactInputSx,
  pageFormCtaGridSx,
  pageFormInlineActionSx,
  pageFormLabelSx,
  pageAssetIdControlRowSx,
  pageSubsectionInputRowSx,
  pageSubsectionLabelSx,
  EMPTY_CTA,
  IN_APP_NOTIFICATION_VALIDATIONS,
} from "../../constants/inAppNotificationConstant";

const CtaEditor = ({
  ctas = [],
  onChange,
  maxCtas = 2,
  title = "Call To Action",
  idPrefix = "cta",
  error,
  compact = true,
  page = false,
  paired = false,
  readOnly = false,
}) => {
  const updateCta = (index, field, value) => {
    onChange(ctas.map((cta, i) => (i === index ? { ...cta, [field]: value } : cta)));
  };

  const addCta = () => {
    if (ctas.length >= maxCtas) return;
    onChange([...ctas, { ...EMPTY_CTA }]);
  };

  const removeCta = (index) => {
    onChange(ctas.filter((_, i) => i !== index));
  };

  const inputSx = page
    ? { width: "100%", minWidth: 0, maxWidth: "100%" }
    : { ...compactInputSx, maxWidth: "100%" };
  const canAddCta = !readOnly && ctas.length < maxCtas;
  const canRemoveCta = !readOnly && ctas.length > 0;
  const pageControlRowSx = page ? pageAssetIdControlRowSx : {};
  const pageCtaGridSx = page
    ? {
        ...pageFormCtaGridSx,
        gridTemplateColumns: readOnly
          ? { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.4fr)" }
          : pageFormCtaGridSx.gridTemplateColumns,
      }
    : null;
  const rowGridSx = page
    ? pageCtaGridSx
    : {
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.4fr) 36px" },
        gap: 1,
        alignItems: "start",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      };

  const sectionTitle = `${title} (${ctas.length}/${maxCtas})`;

  const addCtaButton = canAddCta ? (
    <Button
      variant="primary"
      size="small"
      startIcon={<AddIcon />}
      onClick={addCta}
      sx={page ? pageFormInlineActionSx : { flexShrink: 0, whiteSpace: "nowrap" }}
    >
      Add CTA
    </Button>
  ) : null;

  const renderPageEmptyState = () => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
      —
    </Typography>
  );

  const renderCtaRow = (cta, index, { showAddButton = false } = {}) => (
    <Box
      key={`${idPrefix}-${index}`}
      sx={
        page && !paired
          ? { ...pageSubsectionInputRowSx, width: "100%", minHeight: 0 }
          : { display: "flex", alignItems: "center", gap: 0.75, width: "100%" }
      }
    >
      <Box sx={{ ...rowGridSx, flex: page ? 1 : undefined, minWidth: 0 }}>
        <FormInput
          id={`${idPrefix}-${index}-label`}
          size="small"
          fullWidth
          label={page ? undefined : compact ? "Label" : ""}
          placeholder={page ? "Label" : "e.g., Explore"}
          value={cta.label}
          onChange={(e) => updateCta(index, "label", e.target.value)}
          sx={inputSx}
          disabled={readOnly}
          slotProps={{
            htmlInput: { maxLength: IN_APP_NOTIFICATION_VALIDATIONS.ctaLabel.maxLength },
          }}
        />
        <FormInput
          id={`${idPrefix}-${index}-target`}
          size="small"
          fullWidth
          label={page ? undefined : compact ? "Target" : ""}
          placeholder={page ? "miraie://..." : "e.g., miraie://..."}
          value={cta.target}
          onChange={(e) => updateCta(index, "target", e.target.value)}
          sx={inputSx}
          disabled={readOnly}
          slotProps={{
            htmlInput: { maxLength: IN_APP_NOTIFICATION_VALIDATIONS.ctaTarget.maxLength },
          }}
        />
        {canRemoveCta ? (
          <IconButton
            variant="danger"
            size="small"
            tooltip="Remove CTA"
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => removeCta(index)}
            sx={{ flexShrink: 0, width: 40, height: 40 }}
            aria-label="Remove CTA"
          />
        ) : (
          !readOnly && <Box />
        )}
      </Box>
      {showAddButton && addCtaButton}
    </Box>
  );

  if (page && paired) {
    return (
      <>
        <Box sx={{ ...pageSubsectionLabelSx, gridArea: "ctaLabel" }}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            {sectionTitle}
          </Typography>
        </Box>
        <Box
          sx={{
            gridArea: "ctaControls",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.75,
            ...pageAssetIdControlRowSx,
          }}
        >
          {readOnly && ctas.length === 0 && renderPageEmptyState()}
          {!readOnly && ctas.length === 0 && (
            <Box sx={{ ...pageSubsectionInputRowSx, width: "100%" }}>{addCtaButton}</Box>
          )}
          {ctas.map((cta, index) =>
            renderCtaRow(cta, index, { showAddButton: !readOnly && canAddCta && index === 0 }),
          )}
          {error && (
            <Typography variant="caption" color="error" sx={{ display: "block" }}>
              {error}
            </Typography>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      {page ? (
        <Box sx={pageSubsectionLabelSx}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            {sectionTitle}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0.75,
            mb: 0.75,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 0, fontSize: "0.875rem" }}>
            {sectionTitle}
          </Typography>
          {addCtaButton}
        </Box>
      )}

      {error && !page && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
          {error}
        </Typography>
      )}

      {!page && ctas.length > 0 && (
        <Box
          sx={{
            ...rowGridSx,
            display: { xs: "none", sm: "grid" },
            px: 1,
            py: 0.5,
            mb: 0.5,
            bgcolor: "grey.100",
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            Label
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            Target
          </Typography>
          <Box />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          alignItems: page ? "flex-start" : undefined,
          ...pageControlRowSx,
        }}
      >
        {page && error && (
          <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
            {error}
          </Typography>
        )}
        {page && readOnly && ctas.length === 0 && renderPageEmptyState()}
        {page && !readOnly && ctas.length === 0 && (
          <Box sx={{ ...pageSubsectionInputRowSx, width: "100%" }}>{addCtaButton}</Box>
        )}
        {ctas.map((cta, index) =>
          renderCtaRow(cta, index, {
            showAddButton: page && !readOnly && canAddCta && index === 0,
          }),
        )}
      </Box>
    </Box>
  );
};

export default CtaEditor;
